import { useState, useEffect } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export interface AdminUser {
  id: string;
  user_id: string;
  email: string;
  role: 'admin' | 'super_admin';
  created_at: string;
}

export function useAdminAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [adminRecord, setAdminRecord] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAdminRecord = async (userId: string) => {
    // Use security-definer RPC to bypass RLS chicken-and-egg problem
    const { data } = await supabase.rpc('get_admin_record', { check_user_id: userId });
    if (data && data.length > 0) {
      setAdminRecord(data[0] as AdminUser);
    } else {
      setAdminRecord(null);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        checkAdminRecord(s.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        checkAdminRecord(s.user.id);
      } else {
        setAdminRecord(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    if (!data.user) return { error: 'Login failed' };

    // Use security-definer RPC to bypass RLS
    const { data: adminData } = await supabase.rpc('get_admin_record', { check_user_id: data.user.id });

    if (!adminData || adminData.length === 0) {
      await supabase.auth.signOut();
      return { error: 'Access denied. Your account does not have admin privileges.' };
    }

    setAdminRecord(adminData[0] as AdminUser);
    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setAdminRecord(null);
  };

  const isAdmin = !!session && !!adminRecord;
  const isSuperAdmin = isAdmin && adminRecord?.role === 'super_admin';

  return { user, session, adminRecord, loading, isAdmin, isSuperAdmin, signIn, signOut };
}
